package com.htmlagent.site.components.sections

import androidx.compose.runtime.Composable
import androidx.compose.runtime.DisposableEffect
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import com.htmlagent.shared.session.ArtifactVersionSummary
import com.htmlagent.site.components.widgets.TipSide
import com.htmlagent.site.components.widgets.copyToClipboard
import com.htmlagent.site.components.widgets.Tooltip
import com.htmlagent.site.i18n.ArtifactStrings
import com.htmlagent.site.i18n.LocalStrings
import com.htmlagent.site.state.BackCloses
import com.htmlagent.site.state.chat.ArtifactState
import com.htmlagent.site.state.chat.ChatPresenter
import com.htmlagent.site.state.rememberNarrowScreen
import com.htmlagent.site.state.chat.SectionProgress
import com.htmlagent.site.state.chat.SectionShape
import com.htmlagent.site.theme.AddressStyle
import com.htmlagent.site.theme.ArtifactCodeStyle
import com.htmlagent.site.theme.ArtifactFrameStyle
import com.htmlagent.site.theme.ArtifactPanelStyle
import com.htmlagent.site.theme.BrowserBarStyle
import com.htmlagent.site.theme.BrowserFrameStyle
import com.htmlagent.site.theme.BrowserWindowStyle
import com.htmlagent.site.theme.DownloadLinkStyle
import com.htmlagent.site.theme.EmptyHintStyle
import com.htmlagent.site.theme.EmptyStateStyle
import com.htmlagent.site.theme.EmptyTitleStyle
import com.htmlagent.site.theme.GhostButtonVariant
import com.htmlagent.site.theme.ErrorStyle
import com.htmlagent.site.theme.FullscreenCloseStyle
import com.htmlagent.site.theme.Icons
import com.htmlagent.site.theme.MenuDismissStyle
import com.htmlagent.site.theme.PanelCloseVariant
import com.htmlagent.site.theme.PreviewBodyStyle
import com.htmlagent.site.theme.ToolsMenuGroupStyle
import com.htmlagent.site.theme.ToolsMenuSegmentsStyle
import com.htmlagent.site.theme.ToolsMenuActionsStyle
import com.htmlagent.site.theme.ToolsMenuActionStyle
import com.htmlagent.site.theme.ToolsMenuStyle
import com.htmlagent.site.theme.PreviewOverlayStyle
import com.htmlagent.site.theme.PREVIEW_WIDTH
import com.htmlagent.site.theme.PreviewVeilStyle
import com.htmlagent.site.theme.SegmentActiveStyle
import com.htmlagent.site.theme.SegmentVariant
import com.htmlagent.site.theme.SegmentedStyle
import com.htmlagent.site.theme.SkeletonBarStyle
import com.htmlagent.site.theme.SkeletonBarWritingStyle
import com.htmlagent.site.theme.SkeletonBodyStyle
import com.htmlagent.site.theme.SkeletonGhostStyle
import com.htmlagent.site.theme.SkeletonLabelStyle
import com.htmlagent.site.theme.SkeletonSectionStyle
import com.htmlagent.site.theme.SkeletonStyle
import com.htmlagent.site.theme.SkeletonTailStyle
import com.htmlagent.site.theme.Space
import com.htmlagent.site.theme.Tokens
import com.htmlagent.site.theme.ToolbarStyle
import com.htmlagent.site.theme.TrafficLightStyle
import com.htmlagent.site.theme.TrafficLightsStyle
import com.htmlagent.site.theme.VersionMenuHostStyle
import com.htmlagent.site.theme.VersionMenuStyle
import com.htmlagent.site.theme.VersionTabsStyle
import com.htmlagent.site.theme.labelled
import com.htmlagent.site.theme.named
import com.htmlagent.site.theme.offPane
import com.htmlagent.site.theme.pressed
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.foundation.layout.Spacer
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.css.CSSLengthNumericValue
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.attrsModifier
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.margin
import com.varabyte.kobweb.compose.ui.modifiers.onClick
import com.varabyte.kobweb.compose.ui.modifiers.size
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.components.forms.Button
import com.varabyte.kobweb.silk.components.forms.ButtonSize
import com.varabyte.kobweb.silk.components.icons.CheckIcon
import com.varabyte.kobweb.silk.components.icons.ClipboardIcon
import com.varabyte.kobweb.silk.components.icons.DownloadIcon
import com.varabyte.kobweb.silk.components.navigation.Link
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.toModifier
import kotlinx.browser.document
import kotlinx.browser.window
import kotlinx.coroutines.delay
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Iframe
import org.jetbrains.compose.web.dom.Pre
import org.jetbrains.compose.web.dom.Text
import org.w3c.dom.HTMLElement
import org.w3c.dom.events.Event

/** What part of the artifact is being viewed. */
private enum class View { PREVIEW, CODE }

/**
 * At what width. A design rehearsal, not a device emulator.
 *
 * In `cqw` of the frame and not in percent: the page inside takes this width at once while the
 * window animates towards it, and a percentage would follow the animated window instead.
 */
private enum class Width(val css: String, val icon: String) {
    DESKTOP("100cqw", Icons.Desktop),
    MOBILE("${Tokens.previewMobile.value()}", Icons.Phone),
}

/** Its label lives in the texts, not in the constant: it changes with the language. */
private fun Width.label(t: ArtifactStrings): String = when (this) {
    Width.DESKTOP -> t.desktopWidth
    Width.MOBILE -> t.mobileWidth
}

/**
 * The generated page panel: toolbar, preview, or code, and a skeleton while it is written.
 *
 * It collects its flows in here so re-composition stays confined to the panel: while the agent
 * writes prose, this component is untouched. While the HTML arrives it shows the shape of the
 * page, not the render or the code: half-done, both read as a failure.
 *
 * [off] and [onBackToChat] are the narrow screen, where this panel is the whole window and the
 * conversation is the screen behind it. Both default to the wide one, which passes neither.
 */
@Composable
fun ArtifactPanel(
    presenter: ChatPresenter,
    onSelectVersion: (Int) -> Unit,
    off: Boolean = false,
    onBackToChat: (() -> Unit)? = null,
    onOpenMenu: (() -> Unit)? = null,
) {
    val t = LocalStrings.current
    val state by presenter.artifact.collectAsState()
    val versions by presenter.versions.collectAsState()

    var view by remember { mutableStateOf(View.PREVIEW) }

    // The width toggle is a rehearsal of a screen other than this one, and on a phone there is
    // no other: the panel is 340px wide, so `max-width: 420px` and `max-width: 100%` resolve to
    // the same 340 and the two buttons render the identical page. So the rehearsal is not
    // offered there and the phone shows the phone. The choice is kept rather than overwritten,
    // so widening the window brings back whatever was picked in landscape.
    val narrow = rememberNarrowScreen()
    var chosen by remember { mutableStateOf(Width.DESKTOP) }
    val width = if (narrow) Width.MOBILE else chosen

    Div(
        ArtifactPanelStyle.toModifier().offPane(off).toAttrs {
            attr("role", "region")
            attr("aria-label", t.artifact.regionLabel)
        },
    ) {
        Toolbar(
            state = state,
            versions = versions,
            view = view,
            width = width,
            onView = { view = it },
            onWidth = { chosen = it },
            onSelectVersion = onSelectVersion,
            onDownload = presenter::noteDownload,
            onBackToChat = onBackToChat,
            onOpenMenu = onOpenMenu,
            compact = narrow,
        )

        when (val current = state) {
            // Two thirds of the screen with one line in the middle said nothing about what the
            // space is for. The second line names what will happen here and how long it takes,
            // which is the only expectation anybody has before the first wait.
            ArtifactState.None -> Column(
                modifier = EmptyStateStyle.toModifier(),
                horizontalAlignment = Alignment.CenterHorizontally,
            ) {
                SpanText(t.artifact.emptyState, EmptyTitleStyle.toModifier())
                SpanText(t.artifact.emptyHint, EmptyHintStyle.toModifier())
            }

            is ArtifactState.Writing -> Skeleton(current.sections)

            is ArtifactState.Ready -> when (view) {
                View.PREVIEW -> BrowserWindow(url = current.url, width = width)
                View.CODE -> if (current.html.isNotEmpty()) {
                    Pre(ArtifactCodeStyle.toModifier().toAttrs()) { Text(current.html) }
                } else {
                    Box(EmptyStateStyle.toModifier()) {
                        SpanText(t.artifact.codeNotInSession)
                    }
                }
            }

            is ArtifactState.Discarded -> Box(ErrorStyle.toModifier()) {
                SpanText(current.message)
            }
        }
    }
}

/**
 * View, width, version picker and download.
 *
 * The download always points at a specific version, never at "the latest": otherwise what the
 * user takes away would change as soon as the agent generated another one.
 *
 * [compact] is the phone: the [MobileAppBar], with [ToolsMenu] holding every tool. In a row the
 * tools took two lines of a screen that is there for the page.
 */
@Composable
private fun Toolbar(
    state: ArtifactState,
    versions: List<ArtifactVersionSummary>,
    view: View,
    width: Width,
    onView: (View) -> Unit,
    onWidth: (Width) -> Unit,
    onSelectVersion: (Int) -> Unit,
    onDownload: () -> Unit,
    onBackToChat: (() -> Unit)?,
    onOpenMenu: (() -> Unit)?,
    compact: Boolean,
) {
    val t = LocalStrings.current.artifact
    val ready = state as? ArtifactState.Ready

    // Keyed on the version: the label says a link was copied, and after switching version the
    // one on the clipboard is no longer the one on screen.
    var copied by remember(ready?.version) { mutableStateOf(false) }

    if (compact && onBackToChat != null && onOpenMenu != null) {
        MobileAppBar(
            onPage = true,
            pageAvailable = true,
            onOpenMenu = onOpenMenu,
            onShowChat = onBackToChat,
            onShowPage = {},
        ) {
            if (ready != null) {
                ToolsMenu(
                    ready = ready,
                    versions = versions.sortedBy { it.version },
                    view = view,
                    copied = copied,
                    onView = onView,
                    onSelectVersion = onSelectVersion,
                    onCopy = { copyToClipboard(ready.url) { done -> copied = done } },
                    onDownload = onDownload,
                )
            }
        }
    } else {
        Row(
            modifier = ToolbarStyle.toModifier(),
            verticalAlignment = Alignment.CenterVertically,
        ) {
            ViewSwitch(view = view, enabled = ready != null, onView = onView)
            Spacer()

            if (ready != null) {
                Row(
                    modifier = SegmentedStyle.toModifier()
                        .margin(right = Space.Md)
                        .attrsModifier { attr("role", "group") },
                ) {
                    Width.entries.forEach { candidate ->
                        val label = candidate.label(t)
                        Tooltip(label, TipSide.Bottom) {
                            IconSegment(
                                label,
                                active = width == candidate,
                                icon = candidate.icon,
                            ) { onWidth(candidate) }
                        }
                    }
                }

                // A single version is not a choice.
                if (versions.size > 1) {
                    VersionPicker(
                        versions = versions.sortedBy { it.version },
                        active = ready.version,
                        onSelect = onSelectVersion,
                    )
                }

                // Next to the download and quieter than it: the file is the page, the link only
                // shows it. See `t.chat.whatNowShare`, which is where that difference is spelled out.
                Tooltip(t.copyLinkTitle(ready.label), TipSide.Bottom) {
                    CopyLinkButton(copied) { copyToClipboard(ready.url) { done -> copied = done } }
                }

                Tooltip(t.downloadVersion(ready.label), TipSide.BottomEnd) {
                    DownloadVersionLink(ready, onDownload = onDownload)
                }
            }
        }
    }
}

/** Preview or code, as a pair of segments. */
@Composable
private fun ViewSwitch(view: View, enabled: Boolean, modifier: Modifier = Modifier, onView: (View) -> Unit) {
    val t = LocalStrings.current.artifact
    Row(SegmentedStyle.toModifier().then(modifier).attrsModifier { attr("role", "group") }) {
        Segment(t.previewTab, active = view == View.PREVIEW, enabled = enabled) { onView(View.PREVIEW) }
        Segment(t.codeTab, active = view == View.CODE, enabled = enabled) { onView(View.CODE) }
    }
}

@Composable
private fun CopyLinkButton(copied: Boolean, inMenu: Boolean = false, onCopy: () -> Unit) {
    val t = LocalStrings.current.artifact
    Button(
        onClick = { onCopy() },
        modifier = (if (inMenu) ToolsMenuActionStyle.toModifier() else Modifier)
            .named(if (copied) t.linkCopied else t.copyLink),
        variant = if (inMenu) SegmentVariant else GhostButtonVariant,
        size = ButtonSize.SM,
    ) {
        Row(verticalAlignment = Alignment.CenterVertically) {
            ClipboardIcon(Modifier.size(Tokens.iconSm.value()).margin(right = Space.Sm))
            SpanText(if (copied) t.linkCopied else t.copyLink)
        }
    }
}

@Composable
private fun DownloadVersionLink(ready: ArtifactState.Ready, inMenu: Boolean = false, onDownload: () -> Unit) {
    val t = LocalStrings.current.artifact
    Link(
        path = "${ready.url}/download",
        modifier = (if (inMenu) ToolsMenuActionStyle.toModifier() else DownloadLinkStyle.toModifier())
            .named(t.downloadVersionLabel(ready.label))
            // The download is what the product is for, and the only moment it asks
            // for anything back. The chat says so; see `ChatPresenter.noteDownload`.
            .onClick { onDownload() }
            .attrsModifier { attr("download", "") },
    ) {
        Row(verticalAlignment = Alignment.CenterVertically) {
            DownloadIcon(Modifier.size(Tokens.iconSm.value()).margin(right = Space.Sm))
            SpanText(t.download)
        }
    }
}

/**
 * The page's tools behind one button, on a phone: view, versions, link and download.
 *
 * It closes the way [VersionMenu] does: Escape, a press outside, and focus back on the button
 * that opened it. Choosing a view, a version or the download closes it; copying does not, so
 * "Link copied" can be read.
 */
@Composable
@Suppress("LongParameterList") // One menu gathering what the wide toolbar spreads over a row.
private fun ToolsMenu(
    ready: ArtifactState.Ready,
    versions: List<ArtifactVersionSummary>,
    view: View,
    copied: Boolean,
    onView: (View) -> Unit,
    onSelectVersion: (Int) -> Unit,
    onCopy: () -> Unit,
    onDownload: () -> Unit,
) {
    val t = LocalStrings.current.artifact
    var open by remember { mutableStateOf(false) }
    var host by remember { mutableStateOf<HTMLElement?>(null) }
    var trigger by remember { mutableStateOf<HTMLElement?>(null) }

    DisposableEffect(open) {
        if (!open) {
            onDispose { }
        } else {
            val outside: (Event) -> Unit = { event ->
                val inside = (event.target as? org.w3c.dom.Node)?.let { host?.contains(it) } == true
                if (!inside) open = false
            }
            document.addEventListener("pointerdown", outside)
            onDispose { document.removeEventListener("pointerdown", outside) }
        }
    }

    fun close() {
        open = false
        trigger?.focus()
    }

    Box(
        VersionMenuHostStyle.toModifier().attrsModifier {
            ref { el -> host = el as? HTMLElement; onDispose { host = null } }
            onKeyDown { event -> if (event.key == "Escape" && open) close() }
        },
    ) {
        Button(
            onClick = { open = !open },
            modifier = (if (open) SegmentActiveStyle.toModifier() else Modifier)
                .named(t.moreTools)
                .attrsModifier {
                    attr("aria-expanded", open.toString())
                    attr("aria-haspopup", "true")
                    ref { el -> trigger = el as? HTMLElement; onDispose { trigger = null } }
                },
            variant = GhostButtonVariant,
            size = ButtonSize.SM,
        ) {
            Glyph(Icons.More, size = Tokens.iconSm.value())
        }

        if (open) {
            // Taps on the preview never reach the document - they land inside the iframe - so the
            // listener above missed most of the screen. A layer under the menu catches them.
            Div(MenuDismissStyle.toModifier().onClick { close() }.toAttrs())
            Div(
                ToolsMenuStyle.toModifier().toAttrs {
                    attr("role", "group")
                    attr("aria-label", t.moreTools)
                },
            ) {
                ViewSwitch(view = view, enabled = true, modifier = ToolsMenuSegmentsStyle.toModifier()) {
                    onView(it)
                    close()
                }

                // A single version is not a choice.
                if (versions.size > 1) {
                    Div(
                        ToolsMenuGroupStyle.toModifier().toAttrs {
                            attr("role", "group")
                            attr("aria-label", t.versionsGroup)
                        },
                    ) {
                        versions.forEach { v ->
                            VersionTab(v, active = v.version == ready.version) { onSelectVersion(it); close() }
                        }
                    }
                }

                Div(ToolsMenuActionsStyle.toModifier().toAttrs()) {
                    CopyLinkButton(copied, inMenu = true, onCopy = onCopy)
                    DownloadVersionLink(ready, inMenu = true) {
                        onDownload()
                        close()
                    }
                }
            }
        }
    }
}

/** How many versions fit in the rail before it has to collapse. */
private const val VISIBLE_VERSIONS = 5

/** Three lights on the fake browser bar, and three cards in the sketch of a card section. */
private const val TRAFFIC_LIGHTS = 3
private const val SKETCH_CARDS = 3

/**
 * How long the preview veil waits for the iframe's `load` before lifting on its own.
 *
 * Not a guess at the network: a ceiling on how long a composed page may stay hidden. The
 * iframe is cross-origin and sandboxed without `allow-same-origin`, so its `DOMContentLoaded`
 * is out of reach and this is the only approximation available.
 */
private const val VEIL_MAX_MS = 450L

/**
 * The version picker: up to [VISIBLE_VERSIONS] tabs; beyond that, the last two plus the
 * active one stay one click away and the rest lives in [VersionMenu].
 *
 * The active one is always visible: the rail has to say which version is on screen.
 * `role="group"` and not `tablist`: a `tablist` promises arrow-key navigation that is not here.
 */
@Composable
private fun VersionPicker(versions: List<ArtifactVersionSummary>, active: Int, onSelect: (Int) -> Unit) {
    val t = LocalStrings.current.artifact
    val collapsed = versions.size > VISIBLE_VERSIONS
    val visible = if (!collapsed) versions
    else versions.filterIndexed { i, v -> i >= versions.size - 2 || v.version == active }

    Row(
        modifier = Modifier.margin(right = Space.Md),
        verticalAlignment = Alignment.CenterVertically,
    ) {
        Row(
            modifier = VersionTabsStyle.toModifier().attrsModifier {
                attr("role", "group")
                attr("aria-label", t.versionsGroup)
            },
        ) {
            visible.forEach { v -> VersionTab(v, active = v.version == active, onSelect = onSelect) }
        }

        if (collapsed) {
            VersionMenu(versions = versions, active = active, onSelect = onSelect)
        }
    }
}

@Composable
private fun VersionTab(version: ArtifactVersionSummary, active: Boolean, onSelect: (Int) -> Unit) {
    val t = LocalStrings.current.artifact
    Button(
        onClick = { onSelect(version.version) },
        modifier = (if (active) SegmentActiveStyle.toModifier() else Modifier)
            .labelled(t.viewVersion(version.label))
            .pressed(active),
        variant = SegmentVariant,
        size = ButtonSize.SM,
    ) {
        SpanText("v${version.label}")
    }
}

/**
 * The dropdown with every version.
 *
 * Escape closes it, a click outside closes it, and on closing the focus goes back to the button
 * that opened it; without that, keyboard users land at the top of the document. The
 * click-outside listener only lives while it is open and is removed in the `DisposableEffect`.
 */
@Composable
private fun VersionMenu(versions: List<ArtifactVersionSummary>, active: Int, onSelect: (Int) -> Unit) {
    val t = LocalStrings.current.artifact
    var open by remember { mutableStateOf(false) }
    var host by remember { mutableStateOf<HTMLElement?>(null) }
    var trigger by remember { mutableStateOf<HTMLElement?>(null) }

    DisposableEffect(open) {
        if (!open) {
            onDispose { }
        } else {
            val outside: (Event) -> Unit = { event ->
                val inside = (event.target as? org.w3c.dom.Node)?.let { host?.contains(it) } == true
                if (!inside) open = false
            }
            document.addEventListener("pointerdown", outside)
            onDispose { document.removeEventListener("pointerdown", outside) }
        }
    }

    fun closeAndRestoreFocus() {
        open = false
        trigger?.focus()
    }

    Box(
        VersionMenuHostStyle.toModifier()
            .margin(left = Space.Sm)
            .attrsModifier {
                ref { el -> host = el as? HTMLElement; onDispose { host = null } }
                onKeyDown { event -> if (event.key == "Escape" && open) closeAndRestoreFocus() }
            },
    ) {
        Tooltip(t.allVersions, TipSide.BottomEnd) {
            Button(
                onClick = { open = !open },
                modifier = (if (open) SegmentActiveStyle.toModifier() else Modifier)
                    .named(t.allVersionsLabel(versions.size))
                    .attrsModifier {
                        attr("aria-expanded", open.toString())
                        attr("aria-haspopup", "true")
                        ref { el -> trigger = el as? HTMLElement; onDispose { trigger = null } }
                    },
                variant = SegmentVariant,
                size = ButtonSize.SM,
            ) {
                SpanText(t.versionCount(versions.size))
            }
        }

        if (open) {
            // See [ToolsMenu]: a click on the page lands in the iframe, not in the document.
            Div(MenuDismissStyle.toModifier().onClick { closeAndRestoreFocus() }.toAttrs())
            Div(
                VersionMenuStyle.toModifier().toAttrs {
                    attr("role", "group")
                    attr("aria-label", t.allVersions)
                },
            ) {
                versions.forEach { v ->
                    Button(
                        onClick = { onSelect(v.version); closeAndRestoreFocus() },
                        modifier = (if (v.version == active) SegmentActiveStyle.toModifier() else Modifier)
                            .labelled(t.viewVersion(v.label))
                            .pressed(v.version == active),
                        variant = SegmentVariant,
                        size = ButtonSize.SM,
                    ) {
                        SpanText("v${v.label}")
                    }
                }
            }
        }
    }
}

@Composable
private fun Segment(label: String, active: Boolean, enabled: Boolean, onClick: () -> Unit) {
    Button(
        onClick = { if (enabled) onClick() },
        modifier = (if (active) SegmentActiveStyle.toModifier() else Modifier)
            .pressed(active)
            .attrsModifier { if (!enabled) attr("aria-disabled", "true") },
        variant = SegmentVariant,
        size = ButtonSize.SM,
    ) {
        SpanText(label)
    }
}

@Composable
private fun IconSegment(label: String, active: Boolean, icon: String, onClick: () -> Unit) {
    Button(
        onClick = { onClick() },
        modifier = (if (active) SegmentActiveStyle.toModifier() else Modifier)
            .labelled(label)
            .pressed(active),
        variant = SegmentVariant,
        size = ButtonSize.SM,
    ) {
        Glyph(icon, size = Tokens.iconSm.value())
    }
}

/**
 * The browser window that frames the preview.
 *
 * The frame says at a glance that what is inside is another site, and the address bar shows
 * the artifact's real origin. The iframe points at that origin (never a blob nor a `srcdoc`)
 * and carries `sandbox="allow-scripts"` without `allow-same-origin`: together they void the
 * sandbox and an artifact could read the others on its origin. `referrerpolicy="no-referrer"`
 * keeps the URL, which carries the artifact's public id, from reaching third parties.
 *
 * A button in the bar puts the page over the whole screen: on a phone the frame leaves it a
 * fraction of the screen, and on a desktop it is the way to see it without the workspace around it.
 */
@Composable
private fun BrowserWindow(url: String, width: Width) {
    val t = LocalStrings.current.artifact
    var body by remember { mutableStateOf<HTMLElement?>(null) }

    // Chrome's own hint about leaving full screen cannot be changed, so the way out is ours: a
    // button that only exists while this preview covers the screen.
    var fullscreen by remember { mutableStateOf(false) }
    var overlay by remember { mutableStateOf(false) }

    // The overlay takes a step in the history so the back gesture closes it instead of leaving the
    // app; the browser's own full screen already closes on back.
    BackCloses(open = overlay) {
        body?.let { hideFromTop(it) }
        overlay = false
    }
    DisposableEffect(Unit) {
        val onChange: (Event) -> Unit = {
            fullscreen = body != null && document.fullscreenElement == body
        }
        document.addEventListener("fullscreenchange", onChange)
        onDispose { document.removeEventListener("fullscreenchange", onChange) }
    }

    // Keyed on the url so a new version veils itself again: without the key the second page
    // would inherit the first one's `true` and show white while it loaded.
    var loaded by remember(url) { mutableStateOf(false) }

    // The iframe's `load` fires when the last image has arrived, not when the page can be read:
    // measured at 600 ms of latency per image, the document was composed at 55 ms and `load`
    // came at 665 ms with a single cover photograph, and at 1310 ms with four. The generated
    // CSS is inline, so past this threshold what is under the veil is the finished page waiting
    // for a photograph, and a photograph that lands a moment later is how any site behaves.
    LaunchedEffect(url) {
        delay(VEIL_MAX_MS)
        loaded = true
    }

    Div(BrowserFrameStyle.toModifier().toAttrs()) {
        Div(
            BrowserWindowStyle.toModifier()
                .then(
                    Modifier.styleModifier {
                        property("max-width", width.css)
                        property(PREVIEW_WIDTH, width.css)
                    },
                )
                .toAttrs(),
        ) {
            Row(
                modifier = BrowserBarStyle.toModifier(),
                verticalAlignment = Alignment.CenterVertically,
            ) {
                Row(
                    modifier = TrafficLightsStyle.toModifier()
                        .attrsModifier { attr("aria-hidden", "true") },
                ) {
                    repeat(TRAFFIC_LIGHTS) { Div(TrafficLightStyle.toModifier().toAttrs()) }
                }
                SpanText(url, AddressStyle.toModifier())
                Tooltip(t.fullscreen, TipSide.BottomEnd) {
                    Button(
                        onClick = { cover(body, url) { overlay = true } },
                        modifier = Modifier.named(t.fullscreen),
                        variant = PanelCloseVariant,
                        size = ButtonSize.XS,
                    ) {
                        Glyph(Icons.Expand, size = Tokens.iconSm.value())
                    }
                }
            }

            Div(
                PreviewBodyStyle.toModifier()
                    .then(if (overlay) PreviewOverlayStyle.toModifier() else Modifier)
                    .toAttrs {
                    ref { element -> body = element; onDispose { body = null } }
                },
            ) {
                Iframe(
                    attrs = ArtifactFrameStyle.toModifier().toAttrs {
                        attr("src", url)
                        attr("title", t.previewTitle)
                        // Never next to `allow-same-origin`: it would void the sandbox.
                        attr("sandbox", "allow-scripts")
                        attr("referrerpolicy", "no-referrer")
                        ref { element ->
                            val onLoad: (Event) -> Unit = { loaded = true }
                            element.addEventListener("load", onLoad)
                            onDispose { element.removeEventListener("load", onLoad) }
                        }
                    },
                )
                // Always mounted, so lifting it can fade instead of cutting to the page.
                Div(
                    PreviewVeilStyle.toModifier().toAttrs {
                        attr("data-loaded", loaded.toString())
                        if (loaded) attr("aria-hidden", "true")
                    },
                ) {
                    SpanText(t.previewLoading)
                }
                if (fullscreen || overlay) {
                    Button(
                        onClick = {
                            if (overlay) {
                                // Spends the history step taken on opening; `popstate` closes it.
                                window.history.back()
                            } else {
                                document.exitFullscreen()
                            }
                        },
                        modifier = FullscreenCloseStyle.toModifier().named(t.exitFullscreen),
                        variant = null,
                    ) {
                        Glyph(Icons.Close, size = Tokens.iconSm.value())
                    }
                }
            }
        }
    }
}

/**
 * The sketch of the page being written: one row per real section, read from the markers in the
 * HTML itself.
 *
 * There is no percentage bar because how many sections there will be is unknown until they
 * arrive; the tail of two dimmed bars says "there is more" without claiming how much.
 */
@Composable
private fun Skeleton(sections: List<SectionProgress>) {
    val t = LocalStrings.current.artifact
    Column(
        modifier = SkeletonStyle.toModifier().attrsModifier { attr("aria-live", "polite") },
        verticalArrangement = Arrangement.spacedBy(Space.Xl),
    ) {
        if (sections.isEmpty()) Preparing()

        sections.forEach { section ->
            Row(
                modifier = SkeletonSectionStyle.toModifier().fillMaxWidth(),
                horizontalArrangement = Arrangement.spacedBy(Space.Lg),
            ) {
                Row(
                    modifier = SkeletonLabelStyle.toModifier(),
                    verticalAlignment = Alignment.CenterVertically,
                ) {
                    if (section.done) {
                        CheckIcon(
                            Modifier.size(Tokens.iconXs.value())
                                .color(Tokens.working.value())
                                .margin(right = Space.Sm),
                        )
                    }
                    SpanText(section.label)
                }
                Column(
                    modifier = SkeletonBodyStyle.toModifier().fillMaxWidth(),
                    verticalArrangement = Arrangement.spacedBy(Space.Sm),
                ) {
                    Shape(section)
                }
            }
        }

        if (sections.isNotEmpty()) {
            Column(
                modifier = Modifier.fillMaxWidth(),
                verticalArrangement = Arrangement.spacedBy(Space.Sm),
            ) {
                SpanText(t.moreToCome, SkeletonTailStyle.toModifier())
                Bar(SkeletonGhostStyle.toModifier(), "60%")
                Bar(SkeletonGhostStyle.toModifier(), "35%")
            }
        }
    }
}

/**
 * The silhouette shown before the first section, while the model writes the `<head>` and the
 * stylesheet: nav bar, headline, paragraph and grid pulsing in turn.
 *
 * The offset between pulses makes clear this is a placeholder and not a draft.
 * Every container carries `fillMaxWidth`: a Kobweb `Column` measures what its content measures,
 * and without it percentage widths resolve against an empty box.
 */
@Composable
private fun Preparing() {
    val t = LocalStrings.current.artifact
    Column(
        modifier = Modifier.fillMaxWidth(),
        verticalArrangement = Arrangement.spacedBy(Space.Xl),
    ) {
        SpanText(t.preparing, SkeletonTailStyle.toModifier())

        Row(
            modifier = Modifier.fillMaxWidth(),
            horizontalArrangement = Arrangement.spacedBy(Space.Sm),
        ) {
            Pulse("120px", Tokens.barLine.value(), delay = 0)
            Pulse("56px", Tokens.barLine.value(), delay = 120)
            Pulse("56px", Tokens.barLine.value(), delay = 240)
        }

        Column(
            modifier = Modifier.fillMaxWidth(),
            verticalArrangement = Arrangement.spacedBy(Space.Sm),
        ) {
            Pulse("70%", Tokens.barHeading.value(), delay = 360)
            Pulse("48%", Tokens.barHeading.value(), delay = 480)
            Pulse("58%", Tokens.barMeta.value(), delay = 600)
        }

        Row(
            modifier = Modifier.fillMaxWidth(),
            horizontalArrangement = Arrangement.spacedBy(Space.Sm),
        ) {
            Pulse("1 1 0", Tokens.barCard.value(), delay = 720, grow = true)
            Pulse("1 1 0", Tokens.barCard.value(), delay = 840, grow = true)
            Pulse("1 1 0", Tokens.barCard.value(), delay = 960, grow = true)
        }
    }
}

/**
 * A bar that pulses with a delay of its own.
 *
 * The offset turns bars blinking in unison, which read as an error, into a wave that reads as
 * work. The delay is negative so the animation starts already under way.
 */
@Composable
private fun Pulse(width: String, height: CSSLengthNumericValue, delay: Int, grow: Boolean = false) {
    Bar(
        modifier = SkeletonBarWritingStyle.toModifier()
            .styleModifier { property("animation-delay", "-${delay}ms") },
        width = width,
        height = height,
        grow = grow,
    )
}

/**
 * The shape of each section: a short strip is a nav, three wide bars a hero and a grid means
 * cards. All the same rectangle would read as a list loading.
 *
 * Every `Row` carries `fillMaxWidth`: with no width of its own, children with `flex: 1 1 0`
 * share out zero and the cards collapse.
 */
@Composable
private fun Shape(section: SectionProgress) {
    val style = (if (section.done) SkeletonBarStyle else SkeletonBarWritingStyle).toModifier()

    when (section.shape) {
        SectionShape.BAR -> Row(
            modifier = Modifier.fillMaxWidth(),
            horizontalArrangement = Arrangement.spacedBy(Space.Sm),
        ) {
            Bar(style, "120px")
            Bar(style, "56px")
            Bar(style, "56px")
            Bar(style, "56px")
        }

        SectionShape.HERO -> {
            Bar(style, "78%", height = Tokens.barHeading.value())
            Bar(style, "56%", height = Tokens.barHeading.value())
            Row(
                modifier = Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.spacedBy(Space.Sm),
            ) {
                Bar(style, "128px", height = Tokens.barButton.value())
                Bar(style, "104px", height = Tokens.barButton.value())
            }
        }

        SectionShape.CARDS -> {
            Bar(style, "42%", height = Tokens.barLabel.value())
            Row(
                modifier = Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.spacedBy(Space.Sm),
            ) {
                repeat(SKETCH_CARDS) { Bar(style, "1 1 0", height = Tokens.barCard.value(), grow = true) }
            }
        }
    }
}

@Composable
private fun Bar(
    modifier: Modifier = Modifier,
    width: String,
    height: CSSLengthNumericValue = Tokens.barLine.value(),
    grow: Boolean = false,
) {
    Div(
        modifier.then(
            Modifier.styleModifier {
                if (grow) {
                    property("flex", width)
                    // Without this a flex child never shrinks past its content and columns stop being equal.
                    property("min-width", "0")
                } else {
                    property("width", width)
                }
                property("height", "$height")
                property("flex-shrink", "0")
            },
        ).toAttrs(),
    )
}

/**
 * Puts the preview over the whole screen, in whichever way this browser allows.
 *
 * The installed app is already full screen, and asking Android again made the system bars flash
 * on the way out: there it goes into the top layer, and [BackCloses] lets back close it.
 * iPhone Safari has no full screen for an element, so the page opens in a tab of its own - the
 * same origin and CSP "Copy link" gives.
 */
private fun cover(element: HTMLElement?, url: String, onOverlay: () -> Unit) {
    when {
        element != null && window.matchMedia("(display-mode: fullscreen)").matches -> {
            showOverTop(element)
            onOverlay()
        }
        element != null && document.fullscreenEnabled -> element.requestFullscreen()
        else -> window.open(url, "_blank", "noopener")
    }
}

/**
 * Lifts the preview into the browser's top layer, over the whole window.
 *
 * A `position: fixed` overlay was not enough: the frame's `contain` and the panel's
 * `backdrop-filter` make them the containing block, so it covered the panel and left the rail
 * showing. The top layer ignores both. The element stays where it is in the document, so the
 * iframe does not reload. The attribute is set here and not in the markup because a closed
 * `[popover]` is `display: none`.
 */
private fun showOverTop(element: HTMLElement) {
    element.setAttribute("popover", "manual")
    element.asDynamic().showPopover()
}

/** Takes the preview back out of the top layer; see [showOverTop]. */
private fun hideFromTop(element: HTMLElement) {
    element.asDynamic().hidePopover()
    element.removeAttribute("popover")
}
